import { useEffect, useState } from "react";
import {
  FileText,
  Search,
  Folder,
  Plus,
  Trash2,
  Download,
  Edit,
  BarChart3,
  Presentation,
  FileVideo,
  FileImage,
  Check,
  X,
  Eye,
} from "lucide-react";
import UploadFileModal from "./files/UploadFileModal";
import FileViewerModal from "./files/FileViewerModal";
import { useAuth } from "../../../app/AuthContext";
import { useSchoolProfile } from "../../../app/SchoolProfileContext";
import {
  createMaterial,
  deleteMaterial,
  getMaterials,
  updateMaterial,
  getClasses,
  type MaterialData,
  type ClassData,
} from "../../../lib/api";
import { pdf } from "@react-pdf/renderer";
import LessonPDF from "./CreateLessonAI/LessonPDF";
import { SlidesPDF } from "./CreateSlidesAI/SlidesPDF";
import { downloadProvaAsPDF } from "./EvaluationCreator/pdf/PDFRenderer";

export default function FilesManager() {
  const { token } = useAuth();
  const { activeProfile } = useSchoolProfile();
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [newFileName, setNewFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [viewingFile, setViewingFile] = useState<any | null>(null);
  const [showViewer, setShowViewer] = useState(false);

  const [files, setFiles] = useState<
    Array<{
      id: string;
      name: string;
      type: string;
      size: string;
      date: string;
      class: string;
      icon: any;
      color: string;
      material: MaterialData;
    }>
  >([]);

  // Intentionally depends only on token and activeProfile.id.
  // Other functions used here are stable or safe to omit from deps.
  useEffect(() => {
    const fetchFiles = () => {
      if (token) {
        console.log(
          "FilesManager: Buscando arquivos devido a mudança no token/perfil ou evento materialCreated",
        );
        setLoading(true);
        Promise.all([
          getMaterials(token, activeProfile?.id),
          getClasses(token, activeProfile?.id),
        ])
          .then(([materials, classesData]) => {
            setClasses(classesData);
            const processedFiles = materials.map((m) => {
              const row = materialToRow(m);
              return {
                ...row,
                icon: getIconForType(row.type),
                color: "#1967d2", // Default color
                material: m,
              };
            });
            setFiles(processedFiles);
          })
          .catch(console.error)
          .finally(() => setLoading(false));
      }
    };

    fetchFiles();

    // Listener for custom event
    const handleMaterialCreated = (e: any) => {
      console.log("FilesManager: Evento materialCreated recebido!", e);
      fetchFiles();
    };

    window.addEventListener("materialCreated", handleMaterialCreated);

    return () => {
      window.removeEventListener("materialCreated", handleMaterialCreated);
    };
  }, [token, activeProfile?.id]);

  const getIconForType = (type: string) => {
    switch (type) {
      case "pdf":
        return BarChart3;
      case "document":
        return FileText;
      case "presentation":
        return Presentation;
      case "video":
        return FileVideo;
      case "image":
        return FileImage;
      default:
        return FileText;
    }
  };

  const formatBytes = (bytes: number, decimals = 2) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return "—";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const safeParseJson = (value: string): any | null => {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const materialToRow = (m: MaterialData) => {
    const type = m.type || "file";
    const created = m.created_at ? new Date(m.created_at) : null;
    const meta =
      typeof m.content === "string" ? safeParseJson(m.content) : null;
    const sizeBytes =
      meta && typeof meta === "object" && typeof meta.size_bytes === "number"
        ? meta.size_bytes
        : null;
    const className =
      meta && typeof meta === "object" && typeof meta.class_name === "string"
        ? meta.class_name
        : "Geral";
    return {
      id: m.id,
      name: m.title,
      type,
      size: typeof sizeBytes === "number" ? formatBytes(sizeBytes) : "—",
      date:
        created && !Number.isNaN(created.getTime())
          ? created.toLocaleDateString("pt-BR")
          : "—",
      class: className,
      icon: getIconForType(type),
      color: "#1967d2",
      material: m,
    };
  };

  const handleUploadFile = async (fileData: any) => {
    if (!token) return;
    try {
      setLoading(true);
      const meta = {
        kind: "uploaded_file",
        original_name: fileData.name,
        size_bytes:
          typeof fileData.size_bytes === "number"
            ? fileData.size_bytes
            : undefined,
        class_id: fileData.class_id ?? null,
        class_name: fileData.class ?? "Geral",
        base64: fileData.base64, // Save base64 content
      };
      const payload = {
        title: fileData.name,
        content: JSON.stringify(meta),
        type: fileData.type,
        school_profile_id: activeProfile?.id,
      };
      const created = await createMaterial(payload, token);
      setFiles((prev) => [materialToRow(created), ...prev]);
      window.dispatchEvent(new CustomEvent("materialCreated"));
    } catch (e) {
      console.error(e);
      alert("Erro ao salvar arquivo no banco.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (fileId: string) => {
    if (selectedFiles.includes(fileId)) {
      setSelectedFiles(selectedFiles.filter((id) => id !== fileId));
    } else {
      setSelectedFiles([...selectedFiles, fileId]);
    }
  };

  const handleDeleteFiles = () => {
    if (selectedFiles.length === 0) return;
    const confirm = window.confirm(
      `Deseja deletar ${selectedFiles.length} arquivo(s)?`,
    );
    if (!confirm) return;
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        await Promise.all(selectedFiles.map((id) => deleteMaterial(id, token)));
        setFiles((prev) => prev.filter((f) => !selectedFiles.includes(f.id)));
        setSelectedFiles([]);
      } catch (e) {
        console.error(e);
        alert("Erro ao deletar arquivos.");
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleDownloadFile = async (file: any) => {
    const m = file.material;
    const meta =
      typeof m.content === "string" ? safeParseJson(m.content) : null;

    if (meta && meta.kind === "uploaded_file" && meta.base64) {
      // Base64 file case
      const link = document.createElement("a");
      link.href = meta.base64;
      link.download = meta.original_name || file.name;
      link.click();
    } else if (meta && meta.kind === "ai_lesson_plan") {
      // Lesson Plan -> PDF
      try {
        const blob = await pdf(<LessonPDF lesson={meta.data} />).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${file.name}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Erro ao gerar PDF do plano de aula:", err);
        alert("Erro ao gerar PDF.");
      }
    } else if (meta && meta.kind === "ai_slides") {
      // Slides -> PDF
      try {
        const blob = await pdf(<SlidesPDF data={meta.data} />).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${file.name}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Erro ao gerar PDF dos slides:", err);
        alert("Erro ao gerar PDF.");
      }
    } else if (meta && meta.kind === "ai_evaluation") {
      // Evaluation -> Uses existing PDFRenderer
      downloadProvaAsPDF(meta.formData, meta.data, null);
    } else {
      // Fallback for simple text content or generic JSON
      const blob = new Blob([m.content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${file.name}.txt`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleEditFile = (file: any) => {
    setEditingFileId(file.id);
    setNewFileName(file.name);
  };

  const handleSaveRename = () => {
    if (!editingFileId) return;
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        const updated = await updateMaterial(
          editingFileId,
          { title: newFileName },
          token,
        );
        setFiles((prev) =>
          prev.map((f) =>
            f.id === editingFileId
              ? { ...materialToRow(updated), material: updated }
              : f,
          ),
        );
        setEditingFileId(null);
        setNewFileName("");
      } catch (e) {
        console.error(e);
        alert("Erro ao renomear arquivo.");
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleCancelRename = () => {
    setEditingFileId(null);
    setNewFileName("");
  };

  const handleDeleteFile = (fileId: string, fileName: string) => {
    const confirm = window.confirm(`Deseja deletar "${fileName}"?`);
    if (!confirm) return;
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        await deleteMaterial(fileId, token);
        setFiles((prev) => prev.filter((f) => f.id !== fileId));
        setSelectedFiles((prev) => prev.filter((id) => id !== fileId));
      } catch (e) {
        console.error(e);
        alert("Erro ao deletar arquivo.");
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleViewFile = (file: any) => {
    setViewingFile(file);
    setShowViewer(true);
  };

  const handleSelectAllFiles = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map((file) => file.id));
    }
  };

  const filteredFiles = files.filter(
    (file) =>
      file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.class.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="bg-(--card-bg) rounded-xl shadow-sm border border-(--border-color)">
      <div className="p-6 border-b border-(--border-color)">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-(--text-primary)">
              Meus Arquivos
            </h3>
            <p className="text-sm text-(--text-tertiary)">
              Gerencie todos os seus arquivos em um só lugar
            </p>
          </div>
          <div className="flex items-center gap-3">
            {selectedFiles.length > 0 && (
              <button
                onClick={handleDeleteFiles}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition hover:shadow-md"
                style={{ backgroundColor: "#fce8e6", color: "#d93025" }}
              >
                <Trash2 className="w-4 h-4" />
                Deletar ({selectedFiles.length})
              </button>
            )}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition hover:shadow-md bg-(--primary-color)"
            >
              <Plus className="w-4 h-4" />
              Novo Arquivo
            </button>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-(--text-tertiary)" />
          <input
            type="text"
            placeholder="Buscar arquivos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition bg-(--input-bg) border-(--border-color) text-(--text-primary)"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-sm bg-(--bg-secondary) text-(--text-tertiary)">
              <th className="py-3 px-3 sm:px-6 font-medium">
                <input
                  type="checkbox"
                  checked={
                    selectedFiles.length === filteredFiles.length &&
                    filteredFiles.length > 0
                  }
                  onChange={handleSelectAllFiles}
                  className="w-4 h-4 rounded"
                />
              </th>
              <th className="py-3 px-3 sm:px-6 font-medium">Nome</th>
              <th className="py-3 px-6 font-medium hidden lg:table-cell">
                Turma
              </th>
              <th className="py-3 px-6 font-medium hidden md:table-cell">
                Tamanho
              </th>
              <th className="py-3 px-6 font-medium hidden sm:table-cell">
                Data
              </th>
              <th className="py-3 px-3 sm:px-6 font-medium text-right">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredFiles.map((file) => (
              <tr
                key={file.id}
                className="border-b border-(--border-color) hover:bg-(--bg-accent) transition"
              >
                <td className="py-3 px-3 sm:px-6">
                  <input
                    type="checkbox"
                    checked={selectedFiles.includes(file.id)}
                    onChange={() => handleFileSelect(file.id)}
                    className="w-4 h-4 rounded"
                  />
                </td>
                <td className="py-3 px-3 sm:px-6 min-w-0">
                  {editingFileId === file.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newFileName}
                        onChange={(e) => setNewFileName(e.target.value)}
                        className="px-2 py-1 border rounded bg-(--input-bg) text-(--text-primary) w-full"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${file.color}20` }}
                      >
                        <file.icon
                          className="w-4 h-4 sm:w-5 sm:h-5"
                          style={{ color: file.color }}
                        />
                      </div>
                      <div className="min-w-0 truncate">
                        <p className="font-medium text-(--text-primary) truncate text-sm sm:text-base">
                          {file.name}
                        </p>
                        <p className="text-[10px] sm:text-xs text-(--text-tertiary) truncate">
                          .{file.type} • {file.class}
                        </p>
                      </div>
                    </div>
                  )}
                </td>
                <td className="py-3 px-6 hidden lg:table-cell">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-(--bg-secondary) text-(--text-tertiary)">
                    {file.class}
                  </span>
                </td>
                <td className="py-3 px-6 text-(--text-tertiary) hidden md:table-cell text-sm">
                  {file.size}
                </td>
                <td className="py-3 px-6 text-(--text-tertiary) hidden sm:table-cell text-sm">
                  {file.date}
                </td>
                <td className="py-3 px-3 sm:px-6">
                  <div className="flex items-center justify-end gap-2">
                    {editingFileId === file.id ? (
                      <>
                        <button
                          onClick={handleSaveRename}
                          className="p-2 hover:bg-green-100 text-green-600 rounded-lg transition"
                          title="Salvar"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancelRename}
                          className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition"
                          title="Cancelar"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleViewFile(file)}
                          className="p-2 hover:bg-(--bg-secondary) rounded-lg transition"
                          title="Visualizar"
                        >
                          <Eye className="w-4 h-4 text-(--text-tertiary)" />
                        </button>
                        <button
                          onClick={() => handleDownloadFile(file)}
                          className="p-2 hover:bg-(--bg-secondary) rounded-lg transition"
                          title="Download"
                        >
                          <Download className="w-4 h-4 text-(--text-tertiary)" />
                        </button>
                        <button
                          onClick={() => handleEditFile(file)}
                          className="p-2 hover:bg-(--bg-secondary) rounded-lg transition"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4 text-(--text-tertiary)" />
                        </button>
                        <button
                          onClick={() => handleDeleteFile(file.id, file.name)}
                          className="p-2 hover:bg-(--bg-secondary) rounded-lg transition"
                          title="Deletar"
                        >
                          <Trash2 className="w-4 h-4 text-(--text-tertiary)" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredFiles.length === 0 && (
          <div className="text-center py-12">
            <Folder className="w-16 h-16 mx-auto mb-4 text-(--border-color)" />
            <h4 className="text-lg font-medium mb-2 text-(--text-primary)">
              {searchTerm
                ? "Nenhum arquivo encontrado"
                : "Nenhum arquivo encontrado"}
            </h4>
            <p className="text-sm mb-6 text-(--text-tertiary)">
              {searchTerm
                ? "Tente uma busca diferente"
                : "Comece adicionando seu primeiro arquivo"}
            </p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white mx-auto bg-(--primary-color)"
            >
              <Plus className="w-4 h-4" />
              Adicionar Arquivo
            </button>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-(--border-color) flex items-center justify-between text-sm text-(--text-tertiary)">
        <div>
          {selectedFiles.length > 0 ? (
            <span>
              {selectedFiles.length} de {files.length} arquivos selecionados
            </span>
          ) : (
            <span>{files.length} arquivos encontrados</span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <button
            className="flex items-center gap-1 hover:text-(--text-primary) transition"
            onClick={() => window.print()}
          >
            <FileText className="w-4 h-4" />
            Exportar Lista
          </button>
        </div>
      </div>
      <UploadFileModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUploadFile}
        classes={classes}
      />
      <FileViewerModal
        isOpen={showViewer}
        onClose={() => {
          setShowViewer(false);
          setViewingFile(null);
        }}
        file={viewingFile}
        onDownload={handleDownloadFile}
      />
    </div>
  );
}
